import React, { useState, useEffect } from 'react';
import axios from 'axios';
import url from '../constants/config';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const AdminPendingCoaches = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    axios
      .get(`${url}/admin/coaches/pending`, { headers: getAuthHeader() })
      .then(res => setCoaches(res.data || []))
      .catch(() => setError('Failed to load pending coaches.'))
      .finally(() => setLoading(false));
  }, []);

  const handleApprove = async (email) => {
    setActing(email);
    setActionMsg(null);
    setActionError(null);
    try {
      await axios.patch(
        `${url}/admin/coaches/${encodeURIComponent(email)}/approve`,
        {},
        { headers: getAuthHeader() }
      );
      setCoaches(prev => prev.filter(c => c.universityEmail !== email));
      setActionMsg(`Coach ${email} has been approved.`);
    } catch {
      setActionError(`Failed to approve ${email}.`);
    } finally {
      setActing(null);
    }
  };

  const handleReject = async (email) => {
    if (!window.confirm(`Are you sure you want to reject and delete the account for ${email}?`)) return;
    setActing(email);
    setActionMsg(null);
    setActionError(null);
    try {
      await axios.patch(
        `${url}/admin/coaches/${encodeURIComponent(email)}/reject`,
        {},
        { headers: getAuthHeader() }
      );
      setCoaches(prev => prev.filter(c => c.universityEmail !== email));
      setActionMsg(`Coach ${email} has been rejected and removed.`);
    } catch {
      setActionError(`Failed to reject ${email}.`);
    } finally {
      setActing(null);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '8px', color: '#1e293b', fontSize: '22px' }}>Pending Coach Approvals</h2>
      <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '14px' }}>
        New coach accounts awaiting approval before they can log in.
      </p>

      {actionMsg && (
        <div style={{ background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '10px 16px', marginBottom: '16px', color: '#15803d', fontSize: '14px' }}>
          {actionMsg}
        </div>
      )}
      {actionError && (
        <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '6px', padding: '10px 16px', marginBottom: '16px', color: '#dc2626', fontSize: '14px' }}>
          {actionError}
        </div>
      )}

      {loading && <p style={{ color: '#64748b' }}>Loading…</p>}
      {error && <p style={{ color: '#dc2626' }}>{error}</p>}

      {!loading && !error && coaches.length === 0 && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '32px', textAlign: 'center', color: '#64748b' }}>
          No pending coach accounts.
        </div>
      )}

      {!loading && !error && coaches.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>University Email</th>
                <th style={thStyle}>Personal Email</th>
                <th style={thStyle}>School</th>
                <th style={thStyle}>Division</th>
                <th style={thStyle}>Registered</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {coaches.map(coach => (
                <tr key={coach.universityEmail} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={tdStyle}>{coach.firstName} {coach.lastName}</td>
                  <td style={tdStyle}>{coach.universityEmail}</td>
                  <td style={tdStyle}>{coach.personalEmail || '—'}</td>
                  <td style={tdStyle}>{coach.schoolName || '—'}</td>
                  <td style={tdStyle}>{coach.teamDivision || '—'}</td>
                  <td style={tdStyle}>{formatDate(coach.createdAt)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      disabled={acting === coach.universityEmail}
                      onClick={() => handleApprove(coach.universityEmail)}
                      style={approveBtn}
                    >
                      {acting === coach.universityEmail ? '…' : 'Approve'}
                    </button>
                    <button
                      type="button"
                      disabled={acting === coach.universityEmail}
                      onClick={() => handleReject(coach.universityEmail)}
                      style={rejectBtn}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '10px 14px',
  textAlign: 'left',
  fontWeight: '600',
  color: '#475569',
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: '2px solid #e2e8f0',
};

const tdStyle = {
  padding: '12px 14px',
  color: '#334155',
  verticalAlign: 'middle',
};

const approveBtn = {
  background: '#16a34a',
  color: '#fff',
  border: 'none',
  borderRadius: '5px',
  padding: '6px 14px',
  fontSize: '13px',
  cursor: 'pointer',
  marginRight: '8px',
};

const rejectBtn = {
  background: '#fff',
  color: '#dc2626',
  border: '1px solid #dc2626',
  borderRadius: '5px',
  padding: '6px 14px',
  fontSize: '13px',
  cursor: 'pointer',
};

export default AdminPendingCoaches;
